import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { usePages } from '../../contexts/SiteContext';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { productsWithIngredient } from './shopRules';

/** Zoveel producten naast een stof. */
const SHOWN = 3;

/**
 * "Wat er écht in zit" (Paginateksten › Webshop › Ingrediënten). Links de werkzame stoffen als
 * een grote, rustige lijst; kiezen (klik, tik, toetsenbord of aanwijzen met de muis) toont
 * rechts wat de stof doet en in welke producten hij zit, geteld uit het assortiment.
 * Een stof zonder product valt vanzelf weg; zonder stoffen geen blok.
 */
export function IngredientIndex() {
  const pages = usePages();
  const {
    products
  } = useCatalog();
  const reduce = useReducedMotion();
  const panelId = useId();
  const copy = pages.shop.ingredients;
  const groups = copy.items.map((g, i) => ({
    ...g,
    key: `${i}-${g.name}`,
    products: productsWithIngredient(products, g.match)
  })).filter(g => g.name && g.products.length > 0);
  const [activeKey, setActiveKey] = useState(groups[0]?.key);
  const active = groups.find(g => g.key === activeKey) ?? groups[0];
  if (!active) return null;
  return <section className="bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="ingredients-title" data-id="element-6786" data-mp="3kcwzfyn6qbev">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-6787" data-mp="3gcwhw54shk3l">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-6788" data-mp="3bc8k90m2250e">
          <h2 id="ingredients-title" className={`lg:col-span-7 ${sectionHeading} text-ink`} data-id="element-6789" data-mp="36d5pc8zshf65">
            <HeadingText heading={copy.heading} data-id="element-6790" data-mp="2wpjj6uc5njrw" />
          </h2>
          {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-6791" data-mp="2wrmjq8axovav">{fill(copy.body)}</p>}
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-6792" data-mp="2mrc6inowfnvp">
          <ul className="border-t border-ink/15 lg:col-span-6" data-id="element-6793" data-mp="2mt6ts8nn7s2y">
            {groups.map(g => {
            const isActive = g.key === active.key;
            return <li key={g.key} className="border-b border-ink/15" data-id="element-6794" data-mp="3fg9lohxtd4k8">
                  <button type="button" aria-pressed={isActive} aria-controls={panelId} onClick={() => setActiveKey(g.key)} onMouseEnter={() => setActiveKey(g.key)} className="group grid min-h-[64px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-4 text-left sm:gap-4" data-id="element-6795" data-mp="3kf459m3lux5e">
                    {/* min-w-0 + afbreken: een lange naam duwt het aantal anders buiten beeld op mobiel. */}
                    <span className={`min-w-0 break-words font-display text-[1.5rem] font-light leading-tight transition-[color,transform] duration-200 ease-soft sm:text-[2.2rem] ${isActive ? 'translate-x-2 italic text-clay' : 'text-ink group-hover:translate-x-1'}`} data-id="element-6796" data-mp="1ros9sutt31mx">
                      {g.name}
                    </span>
                    <span className={`whitespace-nowrap text-[13px] tabular-nums ${isActive ? 'text-sage' : 'text-muted'}`} data-id="element-6797" data-mp="18tlic3971rd1">{plural(g.products.length, 'product', 'producten')}</span>
                  </button>
                </li>;
          })}
          </ul>

          <div id={panelId} aria-live="polite" className="lg:col-span-6" data-id="element-6798" data-mp="1dpsl5n6ammqr">
            <AnimatePresence mode="wait" initial={false} data-id="element-6799" data-mp="2t6kpj5h8tpuw">
              <motion.div key={active.key} initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease
            }} className="bg-sand p-7 lg:sticky lg:top-[104px] lg:p-10" data-id="element-6800" data-mp="2t96zfbrlwhga">
                <p className="text-[14px] text-sage" data-id="element-6801" data-mp="3tfu8eirwt5zh">{active.name}</p>
                <p className="mt-3 font-display text-[1.6rem] font-light leading-[1.3] text-ink sm:text-[1.9rem]" data-id="element-6802" data-mp="2au4i3luaeqa">{fill(active.effect)}</p>
                <ul className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-id="element-6803" data-mp="78vvdapibicb">
                  {active.products.slice(0, SHOWN).map(p => <li key={p.id} data-id="element-6804" data-mp="g1z3d3hdv2zk">
                      <Link to={`/webshop/${p.id}`} className="group flex min-h-[72px] items-center gap-4 py-3" data-id="element-6805" data-mp="g30hjgtp45li">
                        <ProductImage src={p.image} alt="" width={56} className="h-16 w-14 shrink-0 bg-shell p-1" data-id="element-6806" data-mp="l00odtyddcdo" />
                        <span className="min-w-0 flex-1" data-id="element-6807" data-mp="pxsjewub9em3">
                          <span className="block text-[13px] text-clay" data-id="element-6808" data-mp="v018vj7z5yjg">{p.brand}</span>
                          <span className="block truncate text-[15px] text-ink group-hover:underline group-hover:underline-offset-4" data-id="element-6809" data-mp="2fhaft40pww1u">{p.name}</span>
                        </span>
                        <span className="flex items-center gap-2 text-[14px] tabular-nums text-muted" data-id="element-6810" data-mp="25ikx590zz7kj">
                          {euro(p.price)}
                          <ArrowRightIcon className="h-4 w-4 text-ink transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6811" data-mp="20lb2neyr45pu" />
                        </span>
                      </Link>
                    </li>)}
                </ul>
                {active.products.length > SHOWN && <p className="mt-4 text-[14px] text-muted" data-id="element-6812" data-mp="2jj5k8hdy3pju">En nog {plural(active.products.length - SHOWN, 'product', 'producten')} in de collectie hierboven.</p>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}